import React from "react";
import { useTranslation } from "react-i18next";

/**
 * Brand-correct status pill for the Documents table.
 *
 * Status values are derived clientside from the document record:
 *   - `alert`    — at least one critical active alert / risk_score === "High"
 *   - `monitor`  — non-critical active alerts / risk_score === "Medium"
 *   - `active`   — analyzed + no active alerts / risk_score ∈ {Low,N/A}
 *   - `inactive` — not yet analyzed / archived
 *
 * Each pill renders the colored dot + label exactly like the Figma maquette
 * (see Brand Kit). Colors use QENVA palette tokens, NEVER raw red/green
 * tailwind defaults.
 */
const STYLES = {
  alert: {
    dotClass:   "bg-qenva-cat-contract",
    pillClass:  "bg-qenva-cat-contract/10 text-qenva-cat-contract border-qenva-cat-contract/25",
  },
  monitor: {
    dotClass:   "bg-qenva-cat-lease",
    pillClass:  "bg-qenva-cat-lease/10 text-qenva-cat-lease border-qenva-cat-lease/25",
  },
  active: {
    dotClass:   "bg-qenva-green",
    pillClass:  "bg-qenva-green/10 text-qenva-green border-qenva-green/25",
  },
  inactive: {
    dotClass:   "bg-slate-400",
    pillClass:  "bg-slate-100 text-slate-500 border-slate-200",
  },
  needs_review: {
    dotClass:   "bg-amber-500",
    pillClass:  "bg-amber-50 text-amber-700 border-amber-200",
  },
};

export function deriveDocStatus(doc) {
  if (!doc) return "inactive";
  const status = (doc.status || "").toLowerCase();
  if (status === "needs_review") return "needs_review";
  if (status !== "completed") return "inactive";
  const risk = (doc.risk_score || "").toLowerCase();
  if (risk === "high") return "alert";
  if (risk === "medium") return "monitor";
  return "active";
}

export default function DocStatusPill({ status, className = "" }) {
  const { t } = useTranslation("documents");
  const style = STYLES[status] || STYLES.inactive;
  return (
    <span
      data-testid={`doc-status-pill-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[12px] font-medium whitespace-nowrap ${style.pillClass} ${className}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dotClass} shrink-0`} />
      <span className="truncate max-w-[120px]" title={t(`status.${status}`)}>
        {t(`status.${status}`)}
      </span>
    </span>
  );
}
